import React, { useState, useMemo } from "react";
import {
  Row,
  Col,
  Container,
  Button,
  OverlayTrigger,
  Tooltip,
  Popover,
} from "react-bootstrap";
import { ViewPractice } from "../Components/Practice/viewpractice";
import { ViewUsers } from "../Components/Users/ViewUser";
import { AddUser } from "../Components/Users/AddUser";
import { ViewServices } from "../Components/Services/viewServices";
import { SetUpPractice } from "../Components/Practice/SetupPractice";
import TemplateList from "../Components/Templates/TemplateList";
import { AddTemplate } from "../Components/Templates/AddTemplate";
import { BankingPayments } from "../Components/BankingPayments/BankingPayments";
import { AddBanking } from "../Components/BankingPayments/AddBankingPayments";
import { Comms } from "../Components/Communications/Comms";
import { ViewOnlineBookings } from "../Components/Practice/ViewOnlineBookings";
import AddEditOnlineBookings from "../Components/Practice/AddEditOnlineBookings";
import { ViewPeople } from "../Components/People/ViewPeople";
import { ViewIntakeSettings } from "../Components/Practice/ViewIntakeSettings";
import "../Components/Accounts/acc-rail.css";

const COMMS_KEYS = ["sms", "email", "accounts", "reports"];

export function Practice() {
  const [activeSection, setActiveSection] = useState("practice");
  const [refresh, setRefresh] = useState(false);
  const [addServiceTrigger, setAddServiceTrigger] = useState(0);
  const [addPeopleTrigger, setAddPeopleTrigger] = useState(0);
  // Tracks which rail item's submenu popover is currently open. Keyed by
  // `${prefix}-${item.key}` so the mobile-strip popover and desktop-rail
  // popover never interfere even though both render simultaneously.
  const [openSubKey, setOpenSubKey] = useState(null);
  const handleRefresh = () => setRefresh((prev) => !prev);

  const sidebarItems = useMemo(
    () => [
      { key: "practice", label: "Practice Information", icon: "fa-light fa-hospital" },
      { key: "users", label: "User Management", icon: "fa-sharp fa-light fa-users" },
      { key: "services", label: "Services & Custom Codes", icon: "fa-sharp fa-light fa-list-check" },
      { key: "banking", label: "Banking & Payments", icon: "fa-sharp fa-light fa-building-columns" },
      {
        key: "reminders",
        label: "Communications",
        icon: "fa-sharp fa-light fa-comments",
        children: [
          { key: "sms", label: "SMS Reminders" },
          { key: "email", label: "Email Reminders" },
          { key: "accounts", label: "Financial Emails" },
          { key: "reports", label: "Report Emails" },
        ],
      },
      { key: "templates", label: "Template Management", icon: "fa-sharp fa-light fa-file-lines" },
      { key: "people", label: "People Management", icon: "fa-light fa-address-book" },
      { key: "calendar", label: "Online Bookings", icon: "fa-sharp fa-light fa-calendar-check" },
      { key: "intake", label: "Intake Form", icon: "fa-light fa-clipboard-user" },
    ],
    [],
  );

  // Communications is a flat rail icon but groups four logical sections —
  // any of sms/email/accounts/reports must highlight the parent icon.
  const isItemActive = (item) => {
    if (item.key === "reminders") {
      return activeSection === "reminders" || COMMS_KEYS.includes(activeSection);
    }
    return activeSection === item.key;
  };

  const handleSubClick = (sub) => {
    setActiveSection(sub.key);
    setOpenSubKey(null);
  };

  const activeLabel = useMemo(() => {
    if (COMMS_KEYS.includes(activeSection)) return "Communications";
    return sidebarItems.find((i) => i.key === activeSection)?.label || "";
  }, [activeSection, sidebarItems]);

  function renderRightSlot() {
    switch (activeSection) {
      case "practice":
        return <SetUpPractice onPracticeUpdate={handleRefresh} />;
      case "users":
        return <AddUser onUserUpdate={handleRefresh} />;
      case "templates":
        return <AddTemplate onTemplateUpdate={handleRefresh} />;
      case "banking":
        return <AddBanking onBankingUpdate={handleRefresh} />;
      case "people":
        return (
          <Button
            variant="primary"
            onClick={() => setAddPeopleTrigger((n) => n + 1)}
          >
            Add Person
          </Button>
        );
      case "calendar":
        return <AddEditOnlineBookings onBookingUpdate={handleRefresh} />;
      default:
        return null;
    }
  }

  function renderContent() {
    switch (activeSection) {
      case "practice":
        return (
          <ViewPractice refresh={refresh} onPracticeUpdate={handleRefresh} />
        );
      case "users":
        return <ViewUsers refresh={refresh} onUserUpdate={handleRefresh} />;
      case "services":
        return (
          <ViewServices
            refresh={refresh}
            onServiceUpdate={handleRefresh}
            addTrigger={addServiceTrigger}
          />
        );
      case "templates":
        return (
          <TemplateList refresh={refresh} onTemplateEdit={handleRefresh} />
        );
      case "banking":
        return (
          <BankingPayments refresh={refresh} onBankingUpdate={handleRefresh} />
        );
      case "calendar":
        return (
          <ViewOnlineBookings
            refresh={refresh}
            onOnlineBookingUpdate={handleRefresh}
          />
        );
      case "sms":
      case "email":
      case "accounts":
      case "reports":
        return <Comms initialTab={activeSection} />;
      case "intake":
        return <ViewIntakeSettings refresh={refresh} onUpdate={handleRefresh} />;
      case "people":
        return (
          <ViewPeople
            refresh={refresh}
            onPeopleUpdate={handleRefresh}
            addTrigger={addPeopleTrigger}
          />
        );
      default:
        return <ViewPractice />;
    }
  }

  function renderSubmenu(item, popoverId) {
    return (
      <Popover id={popoverId} className="acc-rail-submenu">
        <Popover.Body className="p-2" style={{ minWidth: 180 }}>
          {item.children.map((sub) => (
            <Button
              key={sub.key}
              variant={activeSection === sub.key ? "primary" : "outline-primary"}
              size="sm"
              className="w-100 text-start mb-1"
              onClick={() => handleSubClick(sub)}
            >
              {sub.label}
            </Button>
          ))}
        </Popover.Body>
      </Popover>
    );
  }

  // Renders one rail icon. Items with children use a click-popover (no
  // tooltip — the popover shows labeled buttons). Items without children
  // use a tooltip on mobile only; desktop stays icon-only to match
  // billing.jsx.
  function renderRailItem(item, { prefix, tooltipPlacement, popPlacement }) {
    const isActive = isItemActive(item);
    const hasChildren = !!item.children?.length;
    const submenuKey = `${prefix}-${item.key}`;

    const buttonNode = (
      <Button
        aria-label={item.label}
        className={isActive ? "acc-rail-item active" : "acc-rail-item"}
        onClick={() => {
          if (!hasChildren) setActiveSection(item.key);
        }}
      >
        <i className={item.icon} />
      </Button>
    );

    if (hasChildren) {
      return (
        <OverlayTrigger
          key={submenuKey}
          trigger="click"
          rootClose
          show={openSubKey === submenuKey}
          onToggle={(show) => setOpenSubKey(show ? submenuKey : null)}
          placement={popPlacement}
          overlay={renderSubmenu(item, `pop-${submenuKey}`)}
        >
          {buttonNode}
        </OverlayTrigger>
      );
    }

    if (tooltipPlacement) {
      return (
        <OverlayTrigger
          key={submenuKey}
          placement={tooltipPlacement}
          overlay={
            <Tooltip
              id={`tip-${submenuKey}`}
              className="acc-rail-tip"
            >
              {item.label}
            </Tooltip>
          }
        >
          {buttonNode}
        </OverlayTrigger>
      );
    }

    return React.cloneElement(buttonNode, { key: submenuKey });
  }

  const rightSlot = renderRightSlot();

  return (
    <div style={{ marginTop: "55px" }}>
      <Container fluid className="px-0">
        <Row className="g-0">
          {/* MOBILE TAB STRIP (<768px) — horizontal-scroll icon row */}
          <Col xs={12} className="d-flex d-md-none pt-1 g-0">
            <Container className="bg-white g-0 shadow-sm rounded">
              <div className="acc-rail-mobile">
                {sidebarItems.map((item) =>
                  renderRailItem(item, {
                    prefix: "mobile",
                    tooltipPlacement: "top",
                    popPlacement: "bottom",
                  })
                )}
              </div>
            </Container>
          </Col>

          {/* LEFT PANEL — DESKTOP ICON RAIL (>=768px) */}
          <Col xs="auto" className="d-none d-md-block pt-1 g-0" style={{ width: 64 }}>
            <Container className="bg-white vh-100 g-0 p-1 shadow-sm rounded" style={{ width: 64 }}>
              <div className="acc-rail">
                {sidebarItems.map((item) =>
                  renderRailItem(item, {
                    prefix: "desktop",
                    tooltipPlacement: null,
                    popPlacement: "right",
                  })
                )}
              </div>
            </Container>
          </Col>

          {/* RIGHT CONTENT */}
          <Col xs={12} md className="g-0">
            <Container fluid className="pt-3 bg-transparent pb-3 mb-3 rounded">
              <Row className="align-items-center mb-3 g-0">
                <Col>
                  <h4 className="text-primary fw-normal mb-0 ms-1">
                    {activeLabel}
                  </h4>
                </Col>
                {rightSlot && (
                  <Col xs="auto" className="text-end">
                    {rightSlot}
                  </Col>
                )}
              </Row>
              {renderContent()}
            </Container>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
